import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit"
import { AppThunkAPIType } from "../../app/appTypes"
import { RootState } from "../../app/store"

export type ImportStep = 'idle' | 'ordering' | 'waiting' | 'finished'

export type Chunk = {
    id: string
    html: string
    blockType: string
	page: number
	order: number
}

export type MarkerImage = {
    name: string
    content: string
}

const adapter = createEntityAdapter<Chunk>({
    selectId: ch => ch.id
})

const imageAdapter = createEntityAdapter<MarkerImage>({
    selectId: ch => ch.name
})

type Meta = {
    description: string,
    structureId: string
    title: string
    ursNumber: string,
    relatedCompanyName: string
    relatedDocumentId: string
}

type MarkerState = {
    total: number
    current: number
    chunks: EntityState<Chunk>
    images: EntityState<MarkerImage>
	step: ImportStep
	jobId: number | null
    meta?: Meta
}

interface StartImportArg {
    file: File
}

// data StartResponse = StartResponse
//     { ok :: Bool
//     , reqId :: Maybe Int64
//     } deriving (Generic, Show)
type StartImportResponse = {
    ok: boolean
    reqId: number | null
}
export const startImport
    = createAsyncThunk<StartImportResponse | null, StartImportArg, AppThunkAPIType>("marker/start-import", async (arg) => {

    const { file } = arg

    const fd = new FormData()
    fd.append("file", file)

    const response = await fetch(`${process.env.REACT_APP_IMPORT_API}/api/marker/start`, {
        method: 'POST',
        mode: 'cors',
        headers: {
            'Authorization': `bearer ${process.env.REACT_APP_IMPORT_SECRET}`,
        },
        body: fd,
    })

    if (response.ok) {
        const resp =  await response.json() as StartImportResponse
        return resp
    } else {
        return null
    }
})

// newtype CompleteStatus = CompleteStatus { completed :: Bool }
export interface JobStatus {
    completed: boolean
}
export const checkStatus
    = createAsyncThunk<JobStatus, number, AppThunkAPIType>("marker/check-status", async (jobId) => {

    // Scotty.get "/api/marker/job/:id/is-complete" isCompleteHandler
    const response = await fetch(`${process.env.REACT_APP_IMPORT_API}/api/marker/job/${jobId}/is-complete`, {
        method: "GET",
        mode: 'cors',
        headers: {
            'Authorization': `bearer ${process.env.REACT_APP_IMPORT_SECRET}`,
        },
    });

    if (response.ok) {
        const resp =  await response.json() as JobStatus
        return resp
    } else {
        return {
            completed: true
        }
    }
})

const rg = /<img alt="(.*?)" src="(.*?)"\/>/gm

export interface JobResult {
    blocks: Chunk[]
    images: MarkerImage[]
}
export const loadResult
    = createAsyncThunk<JobResult, number, AppThunkAPIType>("marker/load-result", async (jobId) => {

    // Scotty.get "/api/marker/job/:id/result" resultPage
    const response = await fetch(`${process.env.REACT_APP_IMPORT_API}/api/marker/job/${jobId}/result`, {
        method: "GET",
        mode: 'cors',
        headers: {
            'Authorization': `bearer ${process.env.REACT_APP_IMPORT_SECRET}`,
        },
    });

    if (response.ok) {
        const result =  await response.json() as JobResult
        const { images } = result
        for (let i = 0; i < result.blocks.length; i++) {
            const html = result.blocks[i].html
            const withImage = html.replace(rg, (_, alt, filename: string) => {
                const imgContent = images.find(i => i.name === filename)
                if (imgContent) {
                    return `<img alt="${alt}" src="data:image/jpeg;base64,${imgContent.content}"`
                } else {
                    return `<img alt="${alt}" src="${filename}"/>`
                }
            })
            result.blocks[i].html = withImage
        }

        return result
    } else {
		return {
            blocks: [],
            images: [],
        }
    }
})

const initialState: MarkerState = {
    chunks: adapter.getInitialState(),
    images: imageAdapter.getInitialState(),
    total: 0,
    current: 0,
	step: 'idle',
	jobId: null,
}

export const markerSlice = createSlice({
    name: 'marker',
    initialState,
    reducers: {
        resetImport: (state) => {
            state.jobId = null
			state.total = 0
			state.current = 0
			state.step = 'idle'
			adapter.removeAll(state.chunks)
        },
        prevPage: (state) => {
			if (state.current > 0) {
				state.current -= 1
			}
        },
        nextPage: (state) => {
			// if (state.current < state.total) {
				state.current += 1
			// }
        },
    },
    extraReducers: builder => {
		builder.addCase(startImport.pending, (state) => {
			state.step = 'ordering'
		})
		builder.addCase(startImport.fulfilled, (state, action) => {
			state.step = 'waiting'
			state.jobId = action.payload ? action.payload.reqId : null
		})
        builder.addCase(loadResult.fulfilled, (state, action) => {
            adapter.setAll(state.chunks, action.payload.blocks)
            imageAdapter.setAll(state.images, action.payload.images)
			state.step = 'finished'
        })
    },
})

export const { resetImport, prevPage, nextPage } = markerSlice.actions

export const selectMarker = (state: RootState) => state.marker

export const {
    selectAll: selectAllChunks,
    selectById: selectChunkById,
} = adapter.getSelectors((state: RootState) => selectMarker(state).chunks)

export const {
    selectById: selectImageById,
} = imageAdapter.getSelectors((state: RootState) => selectMarker(state).images)

export default markerSlice.reducer
